import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, KeyRound, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { resolveEntitlements } from "@/lib/entitlements";
import type { AffiliateCredentialPublic, Tenant } from "@/lib/types";
import { cn } from "@/lib/utils";

interface GeminiCredentialSectionProps {
  readonly credential: AffiliateCredentialPublic | undefined;
  readonly loadingCredentials: boolean;
  readonly plan: Tenant["plan"];
  readonly proUntil: Tenant["pro_until"];
}

/** Secao "IA de Copy" da conta: BYOK no Free (chave Gemini propria, guardada
 *  no Vault), banner "incluso no seu plano" em Pro/Pro+IA (chave da
 *  plataforma, nunca exposta ao painel). */
export function GeminiCredentialSection({
  credential,
  loadingCredentials,
  plan,
  proUntil,
}: GeminiCredentialSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // resolveEntitlements(...).isPaid ja cai pra free quando pro_until esta
  // vencido (mesmo padrao de ImageGeneratorDrawer.tsx/lib/entitlements.ts) -
  // usar so `plan` cru aqui deixava o banner "incluso no seu plano" aceso e o
  // formulario BYOK escondido mesmo com a assinatura vencida (a Edge Function
  // ai-copy ja usa isPlanExpired corretamente, entao o impacto era so
  // client-side/UX).
  const isPaidPlan = resolveEntitlements(plan, proUntil).isPaid;
  const modelConfigured =
    typeof credential?.config_public?.["model"] === "string" ? credential.config_public["model"] : null;
  const isConfigured = Boolean(credential && credential.status === "valid");

  const handleSaveKey = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const normalizedKey = apiKey.trim();
    setStatus(null);
    setError(null);

    if (!normalizedKey) {
      setError("Cole sua chave da API Gemini (Google AI Studio).");
      return;
    }

    setSaving(true);
    const { error: rpcError } = await supabase.rpc("upsert_gemini_credential", {
      p_api_key: normalizedKey,
    });
    setSaving(false);

    if (rpcError) {
      setError(geminiRpcErrorLabel(rpcError.message));
      return;
    }

    setApiKey("");
    setStatus("Chave Gemini salva. Já pode gerar copy com IA nos seus links.");
    await queryClient.invalidateQueries({ queryKey: ["affiliate-credentials"] });
  };

  return (
    <section
      id="platform-gemini"
      className="overflow-hidden rounded-xl border border-border bg-card"
      aria-labelledby="section-gemini"
    >
      <div className="flex items-center justify-between gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
        <div className="flex items-center gap-2.5">
          <Sparkles size={14} className="text-muted-foreground" />
          <h2
            id="section-gemini"
            className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
          >
            IA de copy
          </h2>
        </div>
        {isPaidPlan ? (
          <span className="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
            incluso no seu plano
          </span>
        ) : (
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold",
              isConfigured
                ? "border-primary/25 bg-primary/10 text-primary"
                : "border-warning/25 bg-warning/10 text-warning",
            )}
          >
            {isConfigured ? "configurada" : "não configurada"}
          </span>
        )}
      </div>

      <div className="space-y-5 p-5">
        {isPaidPlan ? (
          <p className="text-sm text-muted-foreground">
            No seu plano a geração de copy usa a chave Gemini da plataforma - não é preciso configurar nada. Use o
            botão "Gerar copy com IA" em qualquer link.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              No plano Free você usa sua própria chave Gemini (gratuita no Google AI Studio) para gerar copy de
              afiliado. A chave fica guardada no Vault e nunca é exibida de volta.
            </p>

            {!loadingCredentials && credential && (
              <div className="space-y-1 rounded-lg border border-border bg-[hsl(var(--surface)/0.75)] p-3">
                <p className="font-mono text-xs text-foreground">
                  Modelo configurado: {modelConfigured ?? "gemini-2.5-flash-lite"}
                </p>
                <p className="text-xs text-muted-foreground">Status: {credential.status === "valid" ? "válida" : credential.status}</p>
              </div>
            )}

            <form onSubmit={handleSaveKey} className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="space-y-2">
                <Label htmlFor="gemini-api-key" variant="field">
                  Chave da API Gemini
                </Label>
                <Input
                  id="gemini-api-key"
                  type="password"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder="AIza..."
                  autoComplete="off"
                />
              </div>
              <Button type="submit" variant="gold" disabled={saving}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                Salvar chave
              </Button>
            </form>

            {status && (
              <InlineNotice tone="success" icon={<CheckCircle2 size={15} />}>
                {status}
              </InlineNotice>
            )}

            {error && (
              <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
                {error}
              </InlineNotice>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function geminiRpcErrorLabel(message: string): string {
  if (message.includes("invalid_gemini_api_key")) return "Chave Gemini inválida.";
  if (message.includes("tenant_not_found")) return "Tenant ativo não encontrado.";
  return "Não foi possível salvar a chave Gemini agora.";
}

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: "success" | "danger";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "success"
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}
